import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getCurrentCountry from "@MasterBluePrint/Handlers/Common/GetCurrentCountry";
import setCountryCode from "@MasterBluePrint/Store/Components/CustomCountry/Handlers/SetCountryCode";
import { CustomApplicationState } from "@MasterBluePrint/Store/CustomApplicationState";
import merge from "lodash/merge";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { parseHtmlWithScriptRemoval } from "@MasterBluePrint/Overrides/Common/Utilities/parseHtmlContent";

const enum fields {
    content = "content",
    allowedCountries = "allowedCountries",
    daysToKeepCookie = "daysToKeepCookie"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.content]: string;
        [fields.allowedCountries]: string;
        [fields.daysToKeepCookie]: number;
    };
    extendedStyles?: CodeSnippetByCountryStyles;
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        countryCode: state.customCountryState.countryCode
    }
}

const mapDispatchToProps = {
    getCurrentCountry,
    setCountryCode
}

type Props = OwnProps & HasShellContext & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CodeSnippetByCountryStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    instructionText?: TypographyPresentationProps;
}

export const codeSnippetByCountryStyles: CodeSnippetByCountryStyles = {
    wrapper: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
    titleText: {
        variant: "h6",
        css: css`
            margin: 0;
        `,
    },
};

const CodeSnippetByCountry = ({ fields, extendedStyles, shellContext, id, 
    getCurrentCountry, countryCode, setCountryCode
 }: Props) => {
    const [styles] = useState(() => merge(codeSnippetByCountryStyles, extendedStyles));
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // get country code from cookie/api call if not in cookie.
        if (countryCode === ""){
            getCurrentCountry({
                daysToKeepCookie: fields.daysToKeepCookie,
                onSuccess: (code: string) => {
                    setCountryCode({countryCode: code});
                },
            });
        }
    }, []);

    useEffect(() => {
        if (countryCode !== "" && fields.allowedCountries && fields.allowedCountries !== ""){
            const allowedCountryList = fields.allowedCountries.split(",");
            if (allowedCountryList){
                const currentCountryAllowed = allowedCountryList.find(c => c === countryCode);
                if (currentCountryAllowed){
                    if (wrapperRef.current) {
                        const fragment = document.createRange().createContextualFragment(fields.content);
                        wrapperRef.current.innerHTML = "";
                        wrapperRef.current.appendChild(fragment);
                    }
                }
            }
        }
    }, [countryCode])

    return (
        <>
            {shellContext.isInShell && shellContext.isEditing && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText}>{translate("Code Snippet By Country")}</Typography>
                    <Typography {...styles.instructionText}>{siteMessage("CodeSnippet_Instruction")}</Typography>
                </StyledWrapper>
            )}
            {IS_SERVER_SIDE ? (
                // eslint-disable-next-line react/no-danger
                <div>
                    {parseHtmlWithScriptRemoval(fields.content)}
                </div>
            ) : (
                <div id={`snippet-wrapper_${id}`} ref={wrapperRef}></div>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(CodeSnippetByCountry)),
    definition: {
        group: "Basic",
        canAdd: context => context.permissions?.canUseAdvancedFeatures || false,
        fieldDefinitions: [
            {
                name: fields.allowedCountries,
                displayName: "Allowed Countries",
                tooltip: "Enter country codes in a comma-delimited string" + 
                    " for countries that are allowed to use this script. IE: US,CA,MX",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.daysToKeepCookie,
                displayName: "Days to keep cookie",
                tooltip: "Enter the number of days to keep the country code cookie.",
                editorTemplate: "IntegerField",
                defaultValue: 30,
                fieldType: "General",
            },
            {
                name: fields.content,
                displayName: "Code",
                tooltip: "",
                editorTemplate: "CodeSnippetField",
                defaultValue: "",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
